import { Container } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useLocation } from "react-router";

import { useClientContext } from "~/contexts/client-context";
import type {
  Leader_Board,
  PagePreview,
  Pages,
  Schedule as ScheduleData,
  TransformedLeaderboard,
  TransformerSchedule,
} from "~/core";
import type { PageConstructorData } from "~/core/services/page-constructor.service";
import { Accordion } from "~/features/accordion/Accordion";
import { isAccordionBlock } from "~/features/accordion/isAccordionBlock";
import { CallToActionBlock } from "~/features/call-to-action/CallToActionBlock";
import { isCallToActionBlock } from "~/features/call-to-action/isCallToActionBlock";
import { CardGallery } from "~/features/card-gallery/CardGallery";
import { isCardGallery } from "~/features/card-gallery/isCardGallery";
import { EventCard } from "~/features/event-card/EventCard";
import { isEventCard } from "~/features/event-card/isEventCard";
import { Features } from "~/features/features/Features";
import { isFeatures } from "~/features/features/isFeatures";
import { GallerySlider } from "~/features/gallery-slider/GallerySlider";
import { isGallerySlider } from "~/features/gallery-slider/isGallerySlider";
import { GridBlock } from "~/features/grid-block/GridBlock";
import { isGridBlock } from "~/features/grid-block/isGridBlock";
import { GridBlockV2 } from "~/features/grid-block-v2/GridBlockV2";
import { isGridBlockV2 } from "~/features/grid-block-v2/isGridV2Block";
import { isLeaderBoard } from "~/features/leader-board/isLeaderBoard";
import { LeaderBoard } from "~/features/leader-board/LeaderBoard";
import { isMainBannerV4 } from "~/features/main-banner-v4/isMainBannerV4";
import { MainBannerV4 } from "~/features/main-banner-v4/MainBannerV4";
import { isMainBanner } from "~/features/main-bannerV2/isMainBanner";
import { MainBannerV2 } from "~/features/main-bannerV2/MainBannerV2";
import { MainBannerV3 } from "~/features/main-bannerV3";
import { isMainBannerV3 } from "~/features/main-bannerV3/isMainBannerV3";
import { isMainSlider } from "~/features/main-slider/isMainSlider";
import { MainSlider } from "~/features/main-slider/MainSlider";
import { isPageBreadcrumbs } from "~/features/page-breadcrumbs/isPageBreadcrumbs";
import { PageBreadcrumbs } from "~/features/page-breadcrumbs/PageBreadcrumbs";
import { isPageLinks } from "~/features/page-links/isPageLinks";
import { PageLinks } from "~/features/page-links/PageLinks";
import { isPaymentMethods } from "~/features/payment-methods/isPaymentMethods";
import { PaymentMethods } from "~/features/payment-methods/PaymentMethods";
import { isReviewSlider } from "~/features/review-slider/isReviewSlider";
import { ReviewSlider } from "~/features/review-slider/ReviewSlider";
import { RowSlider } from "~/features/row-slider";
import { isRowSlider } from "~/features/row-slider/isRowSlider";
import { RowTabs } from "~/features/row-tabs";
import { isRowTabs } from "~/features/row-tabs/isRowTabs";
import { isSchedule } from "~/features/schedule/isSchedule";
import { Schedule } from "~/features/schedule/Schedule";
import { isSocialNetworks } from "~/features/social-network/isSocialNetworks";
import { SocialNetworks } from "~/features/social-network/SocialNetwork";
import { isSpacer } from "~/features/spacer/isSpacer";
import { Spacer } from "~/features/spacer/Spacer";
import { isSteps } from "~/features/steps/isSteps";
import { Steps } from "~/features/steps/Steps";
import { isTextAndImage } from "~/features/text-and-image/isTextAndImage";
import { TextAndImage } from "~/features/text-and-image/TextAndImage";
import { isTextBlock } from "~/features/text-block/isTextBlock";
import { TextBlock } from "~/features/text-block/TextBlock";
import { isTextHighlighter } from "~/features/text-highlighter/isTextHighlighter";
import { TextHighlighter } from "~/features/text-highlighter/TextHighlighter";
import { isTextWithAnchors } from "~/features/text-with-anchors/isTextWithAnchors";
import { TextWithAnchors } from "~/features/text-with-anchors/TextWithAnchors";
import { isWelcomeOfferBanner } from "~/features/welcome-offer-banner/isWelcomeOfferBanner";
import { WelcomeOfferBanner } from "~/features/welcome-offer-banner/WelcomeOfferBanner";
import { useDirectus } from "~/shared/utils/directus/useDirectus";
import { useAction } from "~/widgets/use-action";

export type PageConstructorProps = {
  data: PageConstructorData;
  pages?: Array<PagePreview>;
};

export const PageConstructor: FC<PageConstructorProps> = ({ data, pages }) => {
  const { getDirectusImage } = useDirectus();
  const { breadcrumbsStaticTranslations } = useClientContext();
  const { page } = data;

  const getUrl = (detailedPage: Pages): string => {
    if (!detailedPage?.slug) {
      return `/`;
    }

    const { segment, slug } = detailedPage;
    if (segment) {
      return `/${segment}/${slug}`;
    } else {
      return `/${slug}`;
    }
  };

  const isActive = (pageItem: PagePreview): boolean => {
    const location = useLocation();
    const pathname = location.pathname;
    const parts = pathname.split("/").filter(Boolean);
    const lastSegment = parts[parts.length - 1] || "/";

    return pageItem.slug === lastSegment;
  };

  const { callAction: onCallAction, isActionInactive } = useAction();

  return page?.translations?.[0]?.data?.map((component, index) => {
    const item = component?.item;
    if (!item) return null;

    if (isRowSlider(item)) {
      return (
        <RowSlider
          key={index}
          data={item}
          isControlInactive={isActionInactive}
          getUrl={getUrl}
          onCallAction={onCallAction}
        />
      );
    }

    if (isMainBannerV3(item)) {
      return (
        <MainBannerV3
          key={index}
          data={item}
          isControlInactive={isActionInactive}
          getUrl={getUrl}
          onCallAction={onCallAction}
        />
      );
    }

    if (isRowTabs(item)) {
      return (
        <RowTabs
          key={index}
          data={item}
          isControlInactive={isActionInactive}
          getUrl={getUrl}
          onCallAction={onCallAction}
        />
      );
    }

    if (isTextBlock(item)) {
      return (
        <TextBlock
          width="100%"
          maxWidth={1270}
          p={{ px: 15, py: 24 }}
          m={{ mx: "auto" }}
          md={{ p: { py: 64 } }}
          data={item}
          key={index}
        />
      );
    }

    if (isMainBanner(item)) {
      return (
        <MainBannerV2
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          getImageUrl={getDirectusImage}
          isControlInactive={isActionInactive}
        />
      );
    }

    if (isSteps(item)) {
      return (
        <Steps
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
        />
      );
    }

    if (isWelcomeOfferBanner(item)) {
      return (
        <WelcomeOfferBanner
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          getImageUrl={getDirectusImage}
          isActionInactive={isActionInactive}
        />
      );
    }

    if (isCardGallery(item)) {
      return (
        <CardGallery
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          getImageUrl={getDirectusImage}
          isControlInactive={isActionInactive}
          width="100%"
          maxWidth={1270}
          m={{ mx: "auto" }}
        />
      );
    }

    if (isAccordionBlock(item)) {
      return (
        <Accordion
          key={index}
          data={item}
          width="100%"
          maxWidth={1270}
          m={{ mx: "auto" }}
          d={{ p: { py: 64 } }}
          p={{ px: 15, py: 24 }}
        />
      );
    }

    if (isCallToActionBlock(item)) {
      return (
        <CallToActionBlock
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          getImageUrl={getDirectusImage}
          isActionInactive={isActionInactive}
        />
      );
    }

    if (isEventCard(item)) {
      return (
        <Container
          maxWidth={1270}
          p={{ px: 15, py: 24 }}
          m={{ mx: "auto" }}
          md={{ p: { py: 80 } }}
        >
          <EventCard
            key={index}
            data={item}
            getUrl={getUrl}
            onCallAction={onCallAction}
            isActionInactive={isActionInactive}
          />
        </Container>
      );
    }

    if (isFeatures(item)) {
      return (
        <Features
          key={index}
          data={item}
          maxWidth={1270}
          width="100%"
          m={{ mx: "auto" }}
        />
      );
    }

    if (isGallerySlider(item)) {
      return (
        <GallerySlider
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
          maxWidth={1270}
          width="100%"
          m={{ mx: "auto" }}
        />
      );
    }

    if (isGridBlock(item)) {
      return (
        <GridBlock
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
          m={{ mx: "auto" }}
          maxWidth={1270}
          width="100%"
        />
      );
    }

    const fetchSpreadSheet = async (
      props: Leader_Board,
    ): Promise<TransformedLeaderboard> => {
      const response = await fetch(
        `/api/google-spreadsheet/leader-board?spreadsheetId=${props.spreadsheet_id}&range=${props.range}`,
      );
      return response.json();
    };

    if (isLeaderBoard(item)) {
      return (
        <Container
          m={{ mx: "auto" }}
          maxWidth={1270}
          width="100%"
          p={{ px: 15 }}
        >
          <LeaderBoard
            key={index}
            data={item}
            fetchSpreadsheet={fetchSpreadSheet}
          />
        </Container>
      );
    }

    if (isPageLinks(item)) {
      return (
        <PageLinks
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
          maxWidth={1270}
          width="100%"
          m={{ mx: "auto" }}
        />
      );
    }

    if (isReviewSlider(item)) {
      return (
        <ReviewSlider
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
          maxWidth={1270}
          m={{ mx: "auto" }}
        />
      );
    }

    const fetchSpreadsheet = async (
      params: ScheduleData,
    ): Promise<TransformerSchedule> => {
      const response = await fetch(
        `/api/google-spreadsheet/schedule?spreadsheetId=${params.spreadsheet_id}&range=${params.range}`,
      );

      return response.json();
    };

    if (isSchedule(item)) {
      return (
        <Schedule
          key={index}
          data={item}
          fetchSpreadsheet={fetchSpreadsheet}
          maxWidth={1270}
          m={{ mx: "auto" }}
          width={"100%"}
        />
      );
    }

    if (isSocialNetworks(item)) {
      return (
        <SocialNetworks
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isActionInactive={isActionInactive}
          maxWidth={1270}
          m={{ mx: "auto" }}
        />
      );
    }

    if (isTextAndImage(item)) {
      return (
        <TextAndImage
          key={index}
          maxWidth={1270}
          p={{ px: 15, py: 24 }}
          md={{ p: { py: 64 } }}
          m={{ mx: "auto" }}
          width={"100%"}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
        />
      );
    }

    if (isTextHighlighter(item)) {
      return (
        <TextHighlighter
          key={index}
          data={item}
          getImageUrl={getDirectusImage}
        />
      );
    }

    if (isPageBreadcrumbs(item)) {
      return (
        <PageBreadcrumbs
          key={index}
          data={data}
          homeLabel={breadcrumbsStaticTranslations?.home_label}
        />
      );
    }

    if (isSpacer(item)) {
      return <Spacer data={item} key={index} />;
    }

    if (isTextWithAnchors(item)) {
      return (
        <TextWithAnchors
          width="100%"
          maxWidth={1270}
          p={{ px: 15, py: 24 }}
          m={{ mx: "auto" }}
          md={{ p: { py: 64 } }}
          getPageUrl={getUrl}
          isActive={isActive}
          data={item}
          pages={pages}
          key={index}
        />
      );
    }

    if (isMainSlider(item)) {
      return (
        <MainSlider
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          getImageUrl={getDirectusImage}
          isControlInactive={isActionInactive}
        />
      );
    }
    if (isGridBlockV2(item)) {
      return (
        <GridBlockV2
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
          m={{ mx: "auto" }}
          maxWidth={1270}
          width="100%"
        />
      );
    }

    if (isPaymentMethods(item)) {
      return (
        <PaymentMethods
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
          m={{ mx: "auto" }}
          maxWidth={1270}
          width="100%"
          p={{ px: 15, py: 24 }}
        />
      );
    }

    if (isMainBannerV4(item)) {
      return (
        <MainBannerV4
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
          getImageUrl={getDirectusImage}
        />
      );
    }
    return null;
  });
};
